import React, { useState, useCallback, useEffect } from 'react'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import LinearProgress from '@material-ui/core/LinearProgress'
import { useSnackbar } from 'notistack'
import ArrowBackOutlinedIcon from 'components/material-symbol/icons/ArrowBack'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import EditEventPagination from 'pages/pga-coach/bookings/events/edit/edit-event-form/edit-event-pagination'
import FormSectionContainer from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-section-container'
import EditBookingTypeSectionNav, {
  EDIT_BOOKING_TYPE_SECTIONS,
  getActiveSectionIndex,
} from './edit-booking-type-section-nav'
import BookingTypeDetails from 'pages/pga-coach/bookings/booking-types/components/booking-type-details'
import SingleOrLessonPack from 'pages/pga-coach/bookings/booking-types/components/single-or-lesson-packs'
import BookingTypePaymentOptions from 'pages/pga-coach/bookings/booking-types/components/booking-type-payment-options'
import BookingTypeAdditionalOptions from 'pages/pga-coach/bookings/booking-types/components/booking-type-additional-options'
import { getFirstErrorMessage } from 'pages/pga-coach/bookings/events/form-validation-utils'

const EditBookingTypeLayout = ({
  history,
  bookingType,
  isCreateFlow = false,
  isLessonPacks,
  setIsLessonPacks,
  isSubmitting,
  isFormValid = true,
  isFormDirty = false,
  coachFacilities,
  coachAvailabilities,
  coachingCenters,
  durationInMinutesOptions,
  categories,
  handleCategoryChange,
  startTimeIncrementOptions,
  paymentAccounts,
  singleLessonPrice,
  errors,
  control,
  register,
  watch,
  status,
  setStatus,
  giftingEnabled,
  setGiftingEnabled,
  trigger,
  onCancel,
  validationErrorHandlerRef,
}) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [activeSection, setActiveSection] = useState(
    EDIT_BOOKING_TYPE_SECTIONS[0].id,
  )
  const activeStepIndex = getActiveSectionIndex(activeSection)
  const isFirstStep = activeStepIndex === 0
  const isLastStep = activeStepIndex === EDIT_BOOKING_TYPE_SECTIONS.length - 1
  const currentSection = EDIT_BOOKING_TYPE_SECTIONS[activeStepIndex]

  const scrollToFormTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    if (!validationErrorHandlerRef) return undefined

    validationErrorHandlerRef.current = formErrors => {
      const errorFields = Object.keys(formErrors)
      const sectionWithError = EDIT_BOOKING_TYPE_SECTIONS.find(section =>
        (section.fields || []).some(field => errorFields.includes(field)),
      )

      if (sectionWithError) {
        setActiveSection(sectionWithError.id)
        scrollToFormTop()
      }
    }

    return () => {
      validationErrorHandlerRef.current = () => {}
    }
  }, [scrollToFormTop, validationErrorHandlerRef])

  const handleSectionClick = useCallback(
    sectionId => {
      setActiveSection(sectionId)
      scrollToFormTop()
    },
    [scrollToFormTop],
  )

  const getStepFields = useCallback(
    section => {
      if (section.id === 'edit-section-payment' && isLessonPacks) {
        return ['paymentAccountId', 'lessonPacks']
      }
      if (section.id === 'edit-section-payment' && !isLessonPacks) {
        return ['price', 'paymentAccountId']
      }
      return section.fields || []
    },
    [isLessonPacks],
  )

  const handlePrevious = useCallback(() => {
    if (!isFirstStep) {
      setActiveSection(EDIT_BOOKING_TYPE_SECTIONS[activeStepIndex - 1].id)
      scrollToFormTop()
    }
  }, [activeStepIndex, isFirstStep, scrollToFormTop])

  const handleNext = useCallback(async () => {
    if (isLastStep) return

    const fields = getStepFields(currentSection)
    const isValid = fields.length ? await trigger(fields) : true

    if (isValid) {
      setActiveSection(EDIT_BOOKING_TYPE_SECTIONS[activeStepIndex + 1].id)
      scrollToFormTop()
      return
    }

    const errorMessage = getFirstErrorMessage(errors || {})

    if (errorMessage) {
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }, [
    activeStepIndex,
    currentSection,
    enqueueSnackbar,
    errors,
    getStepFields,
    isLastStep,
    scrollToFormTop,
    trigger,
  ])

  const submitLabel = isCreateFlow
    ? isLessonPacks
      ? 'Create Lesson Pack'
      : 'Create Booking Type'
    : 'Save'

  const canSubmit = isCreateFlow ? isLastStep : isFormDirty

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
      return
    }
    history.goBack()
  }

  return (
    <Box>
      <Box className={classes.actionBar}>
        <Button
          type="button"
          className={classes.cancelAction}
          startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.saveAction}
          data-cy="submit"
          disabled={isSubmitting || !canSubmit}
        >
          {submitLabel}
        </Button>
      </Box>

      {isSubmitting ? (
        <LinearProgress color="primary" className={classes.loadingStyles} />
      ) : null}

      <Container maxWidth="lg">
        <Box pt={4} pb={3}>
          <Box className={classes.layoutGrid}>
            <Box className={classes.navColumn}>
              <EditBookingTypeSectionNav
                activeSection={activeSection}
                onSectionClick={handleSectionClick}
              />
            </Box>

            <Box className={classes.formColumn}>
              <Box hidden={activeSection !== 'edit-section-details'}>
                <FormSectionContainer
                  id={EDIT_BOOKING_TYPE_SECTIONS[0].id}
                  title={EDIT_BOOKING_TYPE_SECTIONS[0].pageTitle}
                >
                  <SingleOrLessonPack
                    isEditFlow
                    isLessonPacks={isLessonPacks}
                    setIsLessonPacks={setIsLessonPacks}
                    paymentAccounts={paymentAccounts}
                  />
                  <BookingTypeDetails
                    isEditFlow
                    coachFacilities={coachFacilities}
                    coachAvailabilities={coachAvailabilities}
                    coachingCenters={coachingCenters}
                    durationInMinutesOptions={durationInMinutesOptions}
                    categories={categories}
                    handleCategoryChange={handleCategoryChange}
                    startTimeIncrementOptions={startTimeIncrementOptions}
                  />
                </FormSectionContainer>
              </Box>
              <Box hidden={activeSection !== 'edit-section-payment'}>
                <FormSectionContainer
                  id={EDIT_BOOKING_TYPE_SECTIONS[1].id}
                  title={EDIT_BOOKING_TYPE_SECTIONS[1].pageTitle}
                >
                  <BookingTypePaymentOptions
                    isEditFlow
                    errors={errors}
                    control={control}
                    register={register}
                    watch={watch}
                    isLessonPacks={isLessonPacks}
                    paymentAccounts={paymentAccounts}
                    singleLessonPrice={singleLessonPrice}
                  />
                </FormSectionContainer>
              </Box>
              <Box hidden={activeSection !== 'edit-section-options'}>
                <FormSectionContainer
                  id={EDIT_BOOKING_TYPE_SECTIONS[2].id}
                  title={EDIT_BOOKING_TYPE_SECTIONS[2].pageTitle}
                >
                  <BookingTypeAdditionalOptions
                    isEditFlow
                    status={status}
                    setStatus={setStatus}
                    bookingType={bookingType}
                    isLessonPacks={isLessonPacks}
                    giftingEnabled={giftingEnabled}
                    setGiftingEnabled={setGiftingEnabled}
                  />
                </FormSectionContainer>
              </Box>
              <EditEventPagination
                isFirstStep={isFirstStep}
                isLastStep={isLastStep}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  )
}

export default EditBookingTypeLayout
